/** @jsx jsx */
import React, { type AnchorHTMLAttributes, type ComponentPropsWithRef } from 'react';
import { styled as styled2, jsx, css } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { type StyledComponentClass } from 'styled-components';
import Avatar from '@atlaskit/avatar';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { Link } from '@atlassian/react-resource-router';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ReadViewContainer, PlaceholderSpacer } from '../../common/ui/styled.tsx';
import { messages } from './messages.tsx';

export type Project = {
	id: string;
	avatarUrl: string | undefined;
	name: string;
	key: string;
};

export const ProjectReadView = ({
	avatarUrl,
	displayName,
	projectKey,
}: {
	avatarUrl: string | undefined;
	displayName: string;
	projectKey: string;
}) => (
	<ProjectContainer>
		<Avatar appearance="square" size="xsmall" src={avatarUrl} />
		<NeutralLink href={`/browse/${projectKey}`}>{displayName}</NeutralLink>
	</ProjectContainer>
);

export const ProjectsReadView = ({
	projects,
	showPlaceholderIfEmpty = true,
}: {
	projects: Project[];
	showPlaceholderIfEmpty?: boolean;
}) => {
	const { formatMessage } = useIntl();

	const placeholderMessageKey = fg('itsm-project-renaming')
		? formatMessage(messages.placeholderGalaxia)
		: formatMessage(messages.placeholder);

	if (projects.length === 0) {
		return (
			<ReadViewContainer isPlaceholder>
				<PlaceholderSpacer>
					{showPlaceholderIfEmpty ? placeholderMessageKey : null}
				</PlaceholderSpacer>
			</ReadViewContainer>
		);
	}
	return (
		<ReadViewContainer isPlaceholder={false}>
			<ProjectList>
				{projects.map(({ id, avatarUrl, name, key }) => (
					<ProjectReadView key={id} avatarUrl={avatarUrl} displayName={name} projectKey={key} />
				))}
			</ProjectList>
		</ReadViewContainer>
	);
};

type LinkProps = ComponentPropsWithRef<typeof Link>;

const neutralLinkStyles = css({
	color: token('color.text.subtle'),
	'&:hover': {
		color: token('color.text.subtle'),
	},
});
const NeutralLinkNew = ({
	children,
	...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & LinkProps) => {
	return (
		<Link css={neutralLinkStyles} {...props}>
			{children}
		</Link>
	);
};
const NeutralLinkOld: StyledComponentClass<
	AnchorHTMLAttributes<HTMLAnchorElement> & LinkProps,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	any
> =
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	styled(Link)({
		color: token('color.text.subtle'),
		'&:hover': {
			color: token('color.text.subtle'),
		},
	});
const NeutralLink = componentWithFG(
	'enghealth-4761-compiled-migration',
	NeutralLinkNew,
	NeutralLinkOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectList = styled2.div({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectContainer = styled2.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),

	backgroundColor: token('color.background.neutral'),
	paddingTop: token('space.025'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
});
